
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'

// module components
import FtlOrdersFilters from '@/modules/FTLBidsModule/components/FtlOrdersFilters/index.vue'
import ActNumber from '@/modules/FTLModule/components/ActNumber/index.vue'
import FtlMakeRequestModal from '@/modules/FTLModule/components/FtlMakeRequestModal/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { ftl_bids } from '@/core/realisations/ftl_bids.realisation'

// types
import { FtlOrder, FtlGetListParamsCamel } from '@/core/types/ftl_bids.types'

// constants
import { FtlColumns, FTLStatusColors } from '@/core/constants/columns/FTL.columns'
import cut from '@/common/helpers/cut'
import { useFile } from '@/common/composable/useFile'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'
import { FTL } from '@/core/realisations/FTL.realisation'
import { orders } from '@/core/realisations/orders.realisation'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

export default defineComponent({
    components: {
        PageTitle,
        FailedFetch,
        FtlOrdersFilters,
        TheTable,
        TableScrollWrapper,
        Cheap,
        TheButton,
        FileReader,
        ButtonDropdown,
        Modal,
        ActNumber,
        FtlMakeRequestModal,
    },
    setup() {
        const notifications = useNotifications()
        const { saveFile } = useFile()
        const { havePermissions } = useAccount()

        const state = reactive({
            orders: new DataState<FtlOrder>({}),
            is_exporting: false,
            is_quantity_loading: false,
            limit: 50,
            is_mass_excel: false,
            mass_excel_file: [] as File[],
            is_mass_avr: false,
        })

        const requestForPaymentState = reactive({
            isOpened: false,
            ftlOrderId: null as number | null
        })

        const state_form$ = useVuelidate(
            {
                mass_excel_file: { required },
            },
            state
        )

        const filters_state = reactive({
            id: null as string | null,
            dopOrderNumber: null as string | null,
            startTime: null as string | null,
            endTime: null as string | null,
            fromActDate: null as string | null,
            toActDate: null as string | null,
            companyId: null as number | null,
            cityFromId: null as number | null,
            cityToId: null as number | null,
            carId: null as number | null,
            carrierId: null as number | null,
            managerId: null as number | null,
            logistId: null as number | null,
            dispatcherId: null as number | null,
            isNeedCar: null as number | null,
            actNumber: null as string | null,
            takeDate: null as string | null,
            statusIds: [] as number[],
            ftlTypes: [] as number[],
        })

        const setTotalPages = () => {
            const totalPages = Math.ceil(state.orders.pagination.total_items / state.limit)
            state.orders.pagination.total_pages = totalPages
        }

        const getQuantity = async () => {
            try {
                state.is_quantity_loading = true

                const params_quantity = {
                    ...filters_state,
                }

                const quantity = await ftl_bids.getQuantity(params_quantity)
                state.orders.pagination.total_items = quantity.data.quantity

                setTotalPages()
            } catch (error) {
                console.error(error)
            } finally {
                state.is_quantity_loading = false
            }
        }

        const loadFtl = async (page: number) => {
            state.orders.pagination.current_page = page

            try {
                state.orders.is_loading = true

                const params: FtlGetListParamsCamel = {
                    page: state.orders.pagination.current_page,
                    limit: state.limit,
                    ...filters_state,
                }

                const response = await ftl_bids.getFtlList(params)

                state.orders.list = response.data
                // state.orders.pagination.total_pages = response.meta.last_page
                // state.orders.pagination.total_items = response.meta.total

                state.orders.is_failed = false
                state.orders.failure.error_state.is_cancel = false
            } catch (error) {
                state.orders.failure.catchError(error)

                if (!state.orders.failure.error_state.is_cancel) {
                    state.orders.is_failed = true
                }
            } finally {
                if (!state.orders.failure.error_state.is_cancel) {
                    state.orders.is_loading = false
                }
            }
        }

        const applyFilters = (params: FtlGetListParamsCamel) => {
            filters_state.id = params.id
            filters_state.dopOrderNumber = params.dopOrderNumber
            filters_state.carrierId = params.carrierId
            filters_state.carId = params.carId
            filters_state.companyId = params.companyId
            filters_state.startTime = params.startTime
            filters_state.endTime = params.endTime
            filters_state.fromActDate = params.fromActDate
            filters_state.toActDate = params.toActDate
            filters_state.cityFromId = params.cityFromId
            filters_state.cityToId = params.cityToId
            filters_state.managerId = params.managerId
            filters_state.logistId = params.logistId
            filters_state.dispatcherId = params.dispatcherId
            filters_state.isNeedCar = params.isNeedCar
            filters_state.actNumber = params.actNumber
            filters_state.statusIds = params.statusIds
            filters_state.ftlTypes = params.ftlTypes

            if (params.takeDate) {
                const takeDate = new Date(params.takeDate)
                const formattedDate = [
                    String(takeDate.getDate()).padStart(2, '0'),
                    String(takeDate.getMonth() + 1).padStart(2, '0'),
                    takeDate.getFullYear(),
                ].join('-')

                filters_state.takeDate = formattedDate
            } else {
                filters_state.takeDate = null
            }

            getQuantity()
            state.orders.pagination.current_page = 1
            loadFtl(state.orders.pagination.current_page)
        }

        const resetFilters = () => {
            filters_state.id = null
            filters_state.dopOrderNumber = null
            filters_state.carrierId = null
            filters_state.carId = null
            filters_state.companyId = null
            filters_state.startTime = null
            filters_state.endTime = null
            filters_state.fromActDate = null
            filters_state.toActDate = null
            filters_state.cityFromId = null
            filters_state.cityToId = null
            filters_state.managerId = null
            filters_state.logistId = null
            filters_state.dispatcherId = null
            filters_state.actNumber = null
            filters_state.isNeedCar = null
            filters_state.statusIds = []
            filters_state.ftlTypes = []
            filters_state.takeDate = null

            getQuantity()
            state.orders.pagination.current_page = 1
            loadFtl(state.orders.pagination.current_page)
        }

        const onLimitChange = (limit: number) => {
            state.limit = limit
            loadFtl(state.orders.pagination.current_page)
        }

        const exportExcel = async () => {
            if (state.is_exporting) return

            try {
                state.is_exporting = true

                // @ts-ignore
                const response = await orders.exportExcelFTL({ ...filters_state })
                saveFile(response, 'FTL заказы.xlsx')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При выгрузке FTL заказов произошла ошибка',
                })
            } finally {
                state.is_exporting = false
            }
        }

        const throwInBilling = async (id: number) => {
            if (!id) return

            try {
                state.orders.is_loading = true

                await FTL.throwInBilling(id)

                notifications.notify({
                    title: `FTL-заказ (ID: ${id}) успешно прокинут!`,
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: `Ошибка прокидывания FTL-заказа`,
                })
            } finally {
                state.orders.is_loading = false
            }
        }

        const toggleMassExcel = () => {
            state.is_mass_excel = !state.is_mass_excel
        }

        const importActNumbers = async (type: string) => {
            try {
                state.orders.is_loading = true

                const formData = new FormData()
                formData.append('file', state.mass_excel_file[0])
                await orders.importActNumbers(formData)
                toggleMassExcel()
                loadFtl(1)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'Ошибка загрузки Excel',
                })
            } finally {
                state.orders.is_loading = false
            }
        }

        const getLink = (ftl_type: number, id: number) => {
            return ftl_type === 2 ? `/ftl/intracity/${id}` : `/ftl-orders/${id}`
        }

        const toggleMassAvr = () => {
            state.is_mass_avr = !state.is_mass_avr

            if (!state.is_mass_avr) loadFtl(1)
        }

        onBeforeMount(() => {
            getQuantity()
            loadFtl(1)
        })

        const onOpenRequestForPayment = (ftlOrderId: number) => {
            requestForPaymentState.ftlOrderId = ftlOrderId
            requestForPaymentState.isOpened = true
        }

        const onCloseRequestForPayment = () => {
            requestForPaymentState.isOpened = false
            requestForPaymentState.ftlOrderId = null
        }

        return {
            state,
            requestForPaymentState,
            state_form$,
            loadFtl,
            applyFilters,
            resetFilters,
            FtlColumns,
            onLimitChange,
            FTLStatusColors,
            cut,
            exportExcel,
            havePermissions,
            throwInBilling,
            toggleMassExcel,
            importActNumbers,
            getLink,
            toggleMassAvr,
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
            onOpenRequestForPayment,
            onCloseRequestForPayment
        }
    },
})
